import type { Tower } from "@mmotdg/shared";
import * as THREE from "three";
import {
  applyTowerLevelVisual,
  buildTowerModel,
  disposeTowerModel,
  type TowerModelView,
} from "./models/towers/index.js";

interface FlashState {
  start: number;
  duration: number;
  mesh: THREE.Mesh;
}

interface TowerView extends TowerModelView {
  level: number;
  baseY: number;
  bobOffset: number;
  flash: FlashState | null;
  tile: THREE.Mesh;
}

// Pulls the dominant colour from a built tower model so the cell tile under
// the tower matches its actual mesh tint instead of a hard-coded palette.
function readModelTint(model: TowerModelView): number {
  const candidates: THREE.Object3D[] = [model.body, model.group];
  for (const obj of candidates) {
    const m = (obj as THREE.Mesh).material;
    if (!m) continue;
    const mat = Array.isArray(m) ? m[0] : m;
    const c = (mat as THREE.MeshStandardMaterial).color;
    if (c) return c.getHex();
  }
  return 0x888888;
}

// Walk every mesh under the tower group and replace each material with a
// dimmed/desaturated clone. Skips selectionRing (already alpha-managed) and
// any material we've already swapped in (idempotent on repeated calls).
const GREY = new THREE.Color(0x808080);
function dimTowerMeshes(view: TowerModelView): void {
  view.group.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!(mesh as unknown as { isMesh?: boolean }).isMesh) return;
    if (mesh === view.selectionRing) return;
    const m = mesh.material;
    if (!m) return;
    if (Array.isArray(m)) {
      mesh.material = m.map((mat) => dimMaterial(mat));
    } else {
      mesh.material = dimMaterial(m);
    }
  });
}

function dimMaterial(mat: THREE.Material): THREE.Material {
  if ((mat as unknown as { __dimmedClone?: boolean }).__dimmedClone) return mat;
  const clone = mat.clone() as THREE.MeshStandardMaterial & {
    __dimmedClone?: boolean;
    emissive?: THREE.Color;
    emissiveIntensity?: number;
    color?: THREE.Color;
  };
  clone.transparent = true;
  clone.opacity = 0.35;
  clone.depthWrite = false;
  if (clone.color) clone.color.lerp(GREY, 0.7);
  if (clone.emissive) clone.emissive.setHex(0x000000);
  if (clone.emissiveIntensity !== undefined) clone.emissiveIntensity = 0;
  clone.needsUpdate = true;
  (clone as unknown as { __dimmedClone: boolean }).__dimmedClone = true;
  return clone;
}

const FLASH_DURATION_MS = 140;

export class TowerRenderer {
  private readonly group = new THREE.Group();
  private readonly views = new Map<string, TowerView>();
  private selectedId: string | null = null;
  private readonly flashGeo = new THREE.SphereGeometry(0.35, 12, 8);
  private readonly flashMat = new THREE.MeshBasicMaterial({
    color: 0xfff6c5,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  private transform: import("./LaneTransform.js").LaneTransform | null = null;
  private sliceAlive = true;

  constructor(private readonly target: THREE.Object3D) {
    this.target.add(this.group);
  }

  setSliceAlive(alive: boolean): void {
    if (this.sliceAlive === alive) return;
    this.sliceAlive = alive;
    for (const view of this.views.values()) this.applyAliveToView(view);
  }

  private applyAliveToView(view: TowerView): void {
    const tileMat = view.tile.material as THREE.MeshBasicMaterial;
    tileMat.opacity = this.sliceAlive ? 0.55 : 0.2;
    if (this.sliceAlive) {
      tileMat.color.setHex(readModelTint(view));
    } else {
      tileMat.color.setHex(0x4a505a);
    }
    tileMat.needsUpdate = true;

    // Dim the tower body too. Boss-hunt doesn't revive players, so once we
    // flip a tower to "dead", we keep it that way for the rest of the
    // match — no need to bother with restore plumbing.
    if (!this.sliceAlive) dimTowerMeshes(view);
  }

  setTransform(t: import("./LaneTransform.js").LaneTransform | null): void {
    this.transform = t;
  }

  // (helper hoisted below so create() can call it before the class methods.)

  private toWorld(x: number, y: number): { x: number; z: number } {
    if (this.transform) return this.transform.flatToWorld(x, y);
    return { x, z: y };
  }

  private buildCellTile(tower: Tower, color: number): THREE.Mesh {
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    if (!this.transform) {
      const geo = new THREE.PlaneGeometry(0.92, 0.92);
      geo.rotateX(-Math.PI / 2);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(tower.cell.cx + 0.5, 0.02, tower.cell.cy + 0.5);
      return mesh;
    }

    const t = this.transform;
    const gapWorld = 0; // gap comes from SLICE_FRACTION in LaneScene
    const sliceLeft = t.sliceCenterAngle - t.sliceAngularSpan / 2;
    const sliceRight = t.sliceCenterAngle + t.sliceAngularSpan / 2;
    const rIn = t.radiusAt(tower.cell.cx);
    const rOut = t.radiusAt(tower.cell.cx + 1);
    const aLeftRaw = t.worldAngleAt(tower.cell.cy);
    const aRightRaw = t.worldAngleAt(tower.cell.cy + 1);

    const clipLeft = (r: number) =>
      Math.max(sliceLeft + gapWorld / 2 / r, aLeftRaw);
    const clipRight = (r: number) =>
      Math.min(sliceRight - gapWorld / 2 / r, aRightRaw);

    // Build the boundary manually with line segments — Three.js Path.absarc
    // has surprising clockwise behaviour that can wrap the wrong way around
    // and produce a full ring instead of a small cell wedge.
    const arcSegs = 6;
    const shape = new THREE.Shape();
    const aLIn = clipLeft(rIn);
    const aRIn = clipRight(rIn);
    const aLOut = clipLeft(rOut);
    const aROut = clipRight(rOut);
    shape.moveTo(rIn * Math.cos(aLIn), rIn * Math.sin(aLIn));
    // Inner arc (CCW from left to right)
    for (let i = 1; i <= arcSegs; i++) {
      const u = i / arcSegs;
      const a = aLIn + u * (aRIn - aLIn);
      shape.lineTo(rIn * Math.cos(a), rIn * Math.sin(a));
    }
    // Radial line to outer-right
    shape.lineTo(rOut * Math.cos(aROut), rOut * Math.sin(aROut));
    // Outer arc (CW from right back to left)
    for (let i = 1; i <= arcSegs; i++) {
      const u = i / arcSegs;
      const a = aROut + u * (aLOut - aROut);
      shape.lineTo(rOut * Math.cos(a), rOut * Math.sin(a));
    }
    shape.closePath();

    const geo = new THREE.ShapeGeometry(shape, 8);
    // +π/2 so shape (x, y) → world (x, 0, +y), matching flatToWorld's Z sign.
    geo.rotateX(Math.PI / 2);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.y = 0.02;
    return mesh;
  }

  sync(towers: Tower[]): void {
    const seen = new Set<string>();
    for (const tower of towers) {
      seen.add(tower.id);
      let view = this.views.get(tower.id);
      if (!view) view = this.create(tower);
      if (view.level !== tower.level) {
        applyTowerLevelVisual(view, tower.level);
        view.level = tower.level;
      }
    }
    for (const [id, view] of this.views) {
      if (!seen.has(id)) {
        this.group.remove(view.group);
        this.group.remove(view.tile);
        view.tile.geometry.dispose();
        (view.tile.material as THREE.Material).dispose();
        if (view.flash) view.group.remove(view.flash.mesh);
        disposeTowerModel(view);
        this.views.delete(id);
      }
    }
  }

  private create(tower: Tower): TowerView {
    const model = buildTowerModel(tower.type, {
      level: tower.level,
      skinId: tower.skinId,
      withSelectionRing: true,
    });
    {
      const pw = this.toWorld(tower.position.x, tower.position.y);
      model.group.position.set(pw.x, 0, pw.z);
      if (this.transform) model.group.rotation.y = -this.transform.worldAngleAt(tower.position.y);
    }
    this.group.add(model.group);

    // Tinted floor tile shaped exactly like the cell's polar quad so the
    // grid cell underneath reads as "owned".
    const tint = readModelTint(model);
    const tile = this.buildCellTile(tower, tint);
    this.group.add(tile);

    const view: TowerView = {
      ...model,
      level: tower.level,
      baseY: model.body.position.y,
      bobOffset: Math.random() * Math.PI * 2,
      flash: null,
      tile,
    };
    this.views.set(tower.id, view);
    if (!this.sliceAlive) this.applyAliveToView(view);
    return view;
  }

  setSelected(towerId: string | null): void {
    if (this.selectedId && this.selectedId !== towerId) {
      const prev = this.views.get(this.selectedId);
      if (prev) {
        const mat = prev.selectionRing.material as THREE.MeshBasicMaterial;
        mat.opacity = 0;
      }
    }
    this.selectedId = towerId;
    if (towerId) {
      const view = this.views.get(towerId);
      if (view) {
        const mat = view.selectionRing.material as THREE.MeshBasicMaterial;
        mat.opacity = 1;
      }
    }
  }

  flash(towerId: string): void {
    const view = this.views.get(towerId);
    if (!view) return;
    if (view.flash) {
      view.group.remove(view.flash.mesh);
      (view.flash.mesh.material as THREE.MeshBasicMaterial).dispose();
    }
    const mat = this.flashMat.clone();
    mat.opacity = 1;
    const mesh = new THREE.Mesh(this.flashGeo, mat);
    mesh.position.copy(view.body.position);
    mesh.position.y = view.baseY + 0.4;
    view.group.add(mesh);
    view.flash = { start: performance.now(), duration: FLASH_DURATION_MS, mesh };
  }

  render(_towers: Tower[], dt: number, time: number): void {
    const now = performance.now();
    for (const [id, view] of this.views) {
      view.body.rotation.y += dt * 0.6;
      const bob = Math.sin(time * 1.4 + view.bobOffset) * 0.025;
      view.body.position.y = view.baseY + bob;
      if (id === this.selectedId) {
        const mat = view.selectionRing.material as THREE.MeshBasicMaterial;
        mat.opacity = 0.55 + Math.sin(time * 5) * 0.35;
        view.selectionRing.rotation.y += dt * 0.8;
      }
      if (view.flash) {
        const elapsed = now - view.flash.start;
        const k = elapsed / view.flash.duration;
        if (k >= 1) {
          view.group.remove(view.flash.mesh);
          (view.flash.mesh.material as THREE.MeshBasicMaterial).dispose();
          view.flash = null;
        } else {
          const mat = view.flash.mesh.material as THREE.MeshBasicMaterial;
          mat.opacity = 1 - k;
          const scale = 1 + k * 1.8;
          view.flash.mesh.scale.set(scale, scale, scale);
        }
      }
    }
  }

  dispose(): void {
    this.target.remove(this.group);
    for (const view of this.views.values()) {
      if (view.flash) {
        view.group.remove(view.flash.mesh);
        (view.flash.mesh.material as THREE.MeshBasicMaterial).dispose();
      }
      disposeTowerModel(view);
    }
    this.views.clear();
    this.flashGeo.dispose();
    this.flashMat.dispose();
  }
}
